/**
 * Barcode detection service.
 * Wraps the Quagga2 library for browser-based barcode detection.
 *
 * Supports UPC-A, UPC-E, EAN-13, EAN-8, Code 128 barcodes.
 * Only returns barcodes with valid checksums to reduce false positives.
 */

import Quagga from '@ericblade/quagga2';
import type { QuaggaResult } from '@ericblade/quagga2';
import type { DetectedMarker, Point2D } from './types';

/**
 * Barcode detector. Stateless — each call to detect() is independent.
 */
export class BarcodeDetector {
  /**
   * Detect barcodes in an ImageData frame.
   * Returns all valid barcodes found (typically 0-2 per frame).
   * Filters out low-confidence detections to reduce false positives.
   */
  async detect(imageData: ImageData): Promise<DetectedMarker[]> {
    return new Promise((resolve) => {
      const detectedMarkers: DetectedMarker[] = [];

      // Create a temporary canvas for Quagga
      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve([]);
        return;
      }

      ctx.putImageData(imageData, 0, 0);

      Quagga.decodeSingle(
        {
          src: canvas.toDataURL(),
          numOfWorkers: 0, // Run in main thread for better performance in browser
          decoder: {
            readers: [
              'upc_reader', // UPC-A and UPC-E
              'ean_reader', // EAN-13 and EAN-8
              'code_128_reader', // Code 128
            ],
          },
          locate: true, // Enable barcode localization
          locator: {
            halfSample: false,
            patchSize: 'medium',
          },
        },
        (result: QuaggaResult | null) => {
          if (!result || !result.codeResult) {
            resolve([]);
            return;
          }

          const { code, format } = result.codeResult;

          // Filter out low-confidence detections
          // Quagga doesn't provide a confidence score directly,
          // but we can check if the code is valid
          if (!code || code.length === 0) {
            resolve([]);
            return;
          }

          // Get bounding box corners
          let corners: [Point2D, Point2D, Point2D, Point2D];

          if (result.box) {
            // Quagga provides box as [[x1, y1], [x2, y2], [x3, y3], [x4, y4]]
            const [[x1, y1], [x2, y2], [x3, y3], [x4, y4]] = result.box;
            corners = [
              { x: x1, y: y1 },
              { x: x2, y: y2 },
              { x: x3, y: y3 },
              { x: x4, y: y4 },
            ];
          } else if (result.line) {
            // Fallback: create a box from the detection line
            const line = result.line;
            const lineHeight = 20; // Approximate barcode height in pixels
            corners = [
              { x: line[0].x, y: line[0].y - lineHeight / 2 },
              { x: line[1].x, y: line[1].y - lineHeight / 2 },
              { x: line[1].x, y: line[1].y + lineHeight / 2 },
              { x: line[0].x, y: line[0].y + lineHeight / 2 },
            ];
          } else {
            // Default to center of image if no location data
            const centerX = imageData.width / 2;
            const centerY = imageData.height / 2;
            const size = 50;
            corners = [
              { x: centerX - size, y: centerY - size },
              { x: centerX + size, y: centerY - size },
              { x: centerX + size, y: centerY + size },
              { x: centerX - size, y: centerY + size },
            ];
          }

          detectedMarkers.push({
            markerType: 'barcode',
            markerId: code,
            corners,
          });

          resolve(detectedMarkers);
        },
      );
    });
  }
}
